import type {GridPMWStockMediaItemStorage} from '@Components/base-grid/components/grid-pmwstockmedia-item/grid-pmw-stock-media-item';
import type {AiGeneratedKeywordsResponse, AiGeneratedDescriptionOnlyResponse} from '@Panels/manage-pmw-stock-panels/components/edit-side-panel/edit-side-panel.types';
import type {
  PMWStockBackendAiAnimatedSticker,
  PMWStockBackendAiIcon,
  PMWStockBackendAiSticker,
  PMWStockBackendAnimatedSticker,
  PMWStockBackendData,
  PMWStockBackendEmoji,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendImage,
  PMWStockBackendResult,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
  PMWStockMediaSearchParams,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {ManagePMWStockPanelType, PMWStockItemType} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {StockResponseCarousel} from '@Panels/stock-panel/stock-panel.types';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {arraysEqual} from '@Utils/array.util';
import type {BatchSaveResult} from '@Panels/upload-pmw-stock-media-panel/components/title-and-save-bar/title-bar-and-save-button.types';

export const originalItemHasBeenModified = (changes: Partial<GridPMWStockMediaItemStorage>, storageItem: Partial<GridPMWStockMediaItemStorage>): boolean => {
  return (
    (changes.keywords && !arraysEqual(changes?.keywords, storageItem.keywords ?? [])) ||
    (changes.description !== undefined && changes.description !== storageItem.description) ||
    (changes.isEdited !== undefined && changes.isEdited !== null) ||
    (changes.isHidden !== undefined && changes.isHidden !== null && changes.isHidden !== storageItem.isHidden) ||
    (changes.isMask !== undefined && changes.isMask !== null && changes.isMask !== storageItem.isMask) ||
    (changes.adminScore !== undefined && changes.adminScore !== storageItem.adminScore)
  );
};

export const isChangedItemEqualToOriginal = (originalItem: Partial<GridPMWStockMediaItemStorage>, changedItem: Partial<GridPMWStockMediaItemStorage>): boolean => {
  let same = true;
  if (changedItem.isMask !== undefined) {
    same = same && changedItem.isMask === originalItem.isMask;
  }
  if (changedItem.isHidden !== undefined) {
    same = same && changedItem.isHidden === originalItem.isHidden;
  }
  if (changedItem.keywords !== undefined) {
    same = same && arraysEqual(changedItem.keywords, originalItem.keywords ?? []);
  }

  if (changedItem.adminScore !== undefined) {
    same = same && changedItem.adminScore === originalItem.adminScore;
  }

  if (changedItem.iconCategory !== undefined) {
    same = same && changedItem.iconCategory === originalItem.iconCategory;
  }

  if (changedItem.shapeCategory !== undefined) {
    same = same && changedItem.shapeCategory === originalItem.shapeCategory;
  }

  if (changedItem.description !== undefined) {
    same = same && changedItem.description === originalItem.description;
  }

  return same;
};

export const originalItemHasBeenDeleted = (changes: Partial<GridPMWStockMediaItemStorage>): boolean => {
  return changes.isDeleted !== undefined && changes.isDeleted;
};

const isAnimatedSticker = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.ANIMATED_STICKER;
};

const isStillSticker = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.STILL_STICKER;
};

const isAiSticker = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.AI_STICKER;
};

const isAiAnimatedSticker = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.AI_ANIMATED_STICKER;
};

const isExtractedGettySticker = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.EXTRACTED_GETTY_STICKER;
};

const isPMWStockImage = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.IMAGE;
};

const isShape = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.SHAPE;
};

const isIcon = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.ICON;
};

const isAiIcon = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.AI_ICON;
};

const isEmoji = (item: PMWStockBackendData): boolean => {
  return item.type === PMWStockItemType.EMOJI;
};

export const getGridStorageItemsFromBackendResults = (results: PMWStockBackendData[]): GridPMWStockMediaItemStorage[] => {
  const storageItems: GridPMWStockMediaItemStorage[] = [];

  for (let i = 0; i < results.length; i++) {
    if (isShape(results[i])) {
      const backendShape = results[i] as PMWStockBackendShape;
      storageItems.push({
        scrollToItem: false,
        idPMWStock: {idPMmwStockMedia: backendShape.idPmwStockMedia, idPMWStockShape: backendShape.idPMWStockShape},
        isHidden: backendShape.isHidden,
        keywords: backendShape.keywords,
        isMask: backendShape.isMask,
        stockType: backendShape.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendShape.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendShape.adminScore,
        description: backendShape.description,
        shapeCategory: backendShape.category,
      });
    } else if (isAnimatedSticker(results[i])) {
      const backendAnimatedSticker = results[i] as PMWStockBackendAnimatedSticker;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendAnimatedSticker.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendAnimatedSticker.idPmwStockMedia, idPMWStockAnimatedSticker: backendAnimatedSticker.idPMWStockAnimatedSticker},
        isHidden: backendAnimatedSticker.isHidden,
        keywords: backendAnimatedSticker.keywords,
        stockType: backendAnimatedSticker.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: results[i].idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendAnimatedSticker.adminScore,
        description: backendAnimatedSticker.description,
      });
    } else if (isStillSticker(results[i])) {
      const backendStillSticker = results[i] as PMWStockBackendStillSticker;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendStillSticker.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendStillSticker.idPmwStockMedia, idPMWStockStillSticker: backendStillSticker.idPMWStockStillSticker},
        isHidden: backendStillSticker.isHidden,
        keywords: backendStillSticker.keywords,
        stockType: backendStillSticker.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendStillSticker.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendStillSticker.adminScore,
        description: backendStillSticker.description,
      });
    } else if (isAiSticker(results[i])) {
      const backendAiSticker = results[i] as PMWStockBackendAiSticker;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendAiSticker.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendAiSticker.idPmwStockMedia, idPMWStockAiSticker: backendAiSticker.idPMWStockAiSticker},
        isHidden: backendAiSticker.isHidden,
        keywords: backendAiSticker.keywords,
        stockType: backendAiSticker.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendAiSticker.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendAiSticker.adminScore,
        description: backendAiSticker.description,
      });
    } else if (isAiAnimatedSticker(results[i])) {
      const backendAiAnimatedSticker = results[i] as PMWStockBackendAiAnimatedSticker;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendAiAnimatedSticker.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendAiAnimatedSticker.idPmwStockMedia, idPMWStockAiAnimatedSticker: backendAiAnimatedSticker.idPMWStockAiAnimatedSticker},
        isHidden: backendAiAnimatedSticker.isHidden,
        keywords: backendAiAnimatedSticker.keywords,
        stockType: backendAiAnimatedSticker.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendAiAnimatedSticker.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendAiAnimatedSticker.adminScore,
        description: backendAiAnimatedSticker.description,
      });
    } else if (isExtractedGettySticker(results[i])) {
      const backendExtractedGettySticker = results[i] as PMWStockBackendExtractedGettySticker;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendExtractedGettySticker.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendExtractedGettySticker.idPmwStockMedia},
        isHidden: backendExtractedGettySticker.isHidden,
        keywords: backendExtractedGettySticker.keywords,
        stockType: backendExtractedGettySticker.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendExtractedGettySticker.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendExtractedGettySticker.adminScore,
        description: backendExtractedGettySticker.description,
      });
    } else if (isPMWStockImage(results[i])) {
      const backendPMWStockImage = results[i] as PMWStockBackendImage;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendPMWStockImage.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendPMWStockImage.idPmwStockMedia, idPMWStockImage: backendPMWStockImage.idPMWStockImage},
        isHidden: backendPMWStockImage.isHidden,
        keywords: backendPMWStockImage.keywords,
        stockType: backendPMWStockImage.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendPMWStockImage.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendPMWStockImage.adminScore,
        description: backendPMWStockImage.description,
      });
    } else if (isIcon(results[i])) {
      const backendIcon = results[i] as PMWStockBackendIcon;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendIcon.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendIcon.idPmwStockMedia, idPMWStockIcon: backendIcon.idPMWStockIcon},
        isHidden: backendIcon.isHidden,
        keywords: backendIcon.keywords,
        stockType: backendIcon.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendIcon.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendIcon.adminScore,
        iconCategory: backendIcon.category,
        description: backendIcon.description,
      });
    } else if (isAiIcon(results[i])) {
      const backendAiIcon = results[i] as PMWStockBackendAiIcon;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendAiIcon.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendAiIcon.idPmwStockMedia, idPMWStockAiIcon: backendAiIcon.idPMWStockAiIcon},
        isHidden: backendAiIcon.isHidden,
        keywords: backendAiIcon.keywords,
        stockType: backendAiIcon.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendAiIcon.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendAiIcon.adminScore,
        iconCategory: backendAiIcon.category,
        description: backendAiIcon.description,
      });
    } else if (isEmoji(results[i])) {
      const backendEmoji = results[i] as PMWStockBackendEmoji;
      storageItems.push({
        scrollToItem: false,
        hashedFilename: backendEmoji.hashedFilename,
        idPMWStock: {idPMmwStockMedia: backendEmoji.idPmwStockMedia, idPMWStockEmoji: backendEmoji.idPMWStockEmoji},
        isHidden: backendEmoji.isHidden,
        keywords: backendEmoji.keywords,
        stockType: backendEmoji.type,
        type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA,
        id: backendEmoji.idPmwStockMedia,
        isEdited: false,
        isDeleted: false,
        adminScore: backendEmoji.adminScore,
        description: backendEmoji.description,
      });
    }
  }
  return storageItems;
};

export const onSearchInput = (txt: string, setSearchTerm: (newTerm: string) => void, setSearchInput: (newTerm: string) => void): void => {
  const newSearchInput = txt.trim();

  if (!newSearchInput) {
    setSearchTerm(newSearchInput);
  }
  setSearchInput(newSearchInput);
};

export const onSearchSubmit = (setSearchTerm: (searchTerm: string) => void, searchInput: string): void => {
  setSearchTerm(searchInput);
};

export const getSearchBarPlaceholder = (areFiltersDisabled: boolean, noKeywordsFilter: boolean): string => {
  if (areFiltersDisabled) {
    return '';
  }
  if (noKeywordsFilter && !areFiltersDisabled) {
    return "Can't Search for Keywords Because Keywords Filter is Set to 'None'";
  }
  return window.i18next.t('pmwjs_manageapp_search_for_keywords');
};

export const savePMWStockChanges = async (changesToSave: Partial<GridPMWStockMediaItemStorage>): Promise<any> => {
  return window.PMW.writeLocal('manageapp/content/updatePMWStockMediaItem', {
    changesToSave: JSON.stringify(changesToSave),
  });
};

export const checkPMWStockChangesSaveStatus = (hashId: string): Promise<any> => {
  return window.PMW.readLocal('manageapp/content/checkPMWStockChangesSaveStatus', {
    hashId,
  });
};

export const uploadNewPMWStockItemToTempS3 = (formData: FormData): Promise<any> => {
  return window.PMW.writeLocal('manageapp/content/uploadNewlyAddedItemsToTempS3Bucket', formData);
};

export const checkNewPMWStockItemUploadStatus = (uniqueStringToPollFor: string): Promise<string | string[]> => {
  return window.PMW.readLocal('manageapp/content/checkPMWStockMediaItemUploadStatus', {
    st: uniqueStringToPollFor,
  }) as Promise<string | string[]>;
};

export const saveUploadedPMWStockItems = (formData: FormData): Promise<any> => {
  return window.PMW.writeLocal('manageapp/content/saveUploadedPMWStockMediaItems', formData);
};

export const checkBatchPMWStockItemSaveStatus = (hashId: string): Promise<BatchSaveResult> => {
  return window.PMW.readLocal('manageapp/content/checkBatchPMWStockItemSaveStatus', {
    hashId,
  }) as Promise<BatchSaveResult>;
};

const PANEL_TYPE_TO_STOCK_TYPE: Partial<Record<ManagePMWStockPanelType, PMWStockItemType>> = {
  [ManagePMWStockPanelType.PMW_SHAPES]: PMWStockItemType.SHAPE,
  [ManagePMWStockPanelType.PMW_ANIMATED_STICKERS]: PMWStockItemType.ANIMATED_STICKER,
  [ManagePMWStockPanelType.PMW_STILL_STICKERS]: PMWStockItemType.STILL_STICKER,
  [ManagePMWStockPanelType.PMW_ICONS]: PMWStockItemType.ICON,
  [ManagePMWStockPanelType.PMW_EMOJIS]: PMWStockItemType.EMOJI,
  [ManagePMWStockPanelType.PMW_IMAGES]: PMWStockItemType.IMAGE,
  [ManagePMWStockPanelType.PMW_EXTRACTED_GETTY_STICKERS]: PMWStockItemType.EXTRACTED_GETTY_STICKER,
  [ManagePMWStockPanelType.PMW_AI_STICKERS]: PMWStockItemType.AI_STICKER,
  [ManagePMWStockPanelType.PMW_AI_ICONS]: PMWStockItemType.AI_ICON,
  [ManagePMWStockPanelType.PMW_AI_ANIMATED_STICKERS]: PMWStockItemType.AI_ANIMATED_STICKER,
};

export const getStockTypeForPanelType = (panelType: ManagePMWStockPanelType): PMWStockItemType | null => {
  return PANEL_TYPE_TO_STOCK_TYPE[panelType] ?? null;
};

export const triggerAiKeywordsGeneration = async (thumbUrls: string[], keywords: string[][]): Promise<string> => {
  return (await window.PMW.writeLocal('manageapp/content/getAiGeneratedKeywordsForPmwStockMediaItems', {
    urls: JSON.stringify(thumbUrls),
    keywords: JSON.stringify(keywords),
  })) as Promise<string>;
};

export const checkAiKeywordsGenerationStatus = async (hashId: string): Promise<AiGeneratedKeywordsResponse[]> => {
  return (await window.PMW.readLocal('manageapp/content/getAiGeneratedKeywordsForPmwStockMediaItemsStatus', {
    hashId,
  })) as Promise<AiGeneratedKeywordsResponse[]>;
};

export const triggerAiDescriptionGeneration = async (thumbUrls: string[], keywords: string[][], stockType: PMWStockItemType | null): Promise<string> => {
  return (await window.PMW.writeLocal('manageapp/content/getAiGeneratedDescriptionForPmwStockMediaItems', {
    urls: JSON.stringify(thumbUrls),
    keywords: JSON.stringify(keywords),
    ...(stockType !== null && {stockType}),
  })) as Promise<string>;
};

export const checkAiDescriptionGenerationStatus = async (hashId: string): Promise<AiGeneratedDescriptionOnlyResponse[]> => {
  return (await window.PMW.readLocal('manageapp/content/getAiGeneratedDescriptionForPmwStockMediaItemsStatus', {
    hashId,
  })) as Promise<AiGeneratedDescriptionOnlyResponse[]>;
};

export const fetchPMWStockMedia = async (params: Partial<PMWStockMediaSearchParams>): Promise<string> => {
  return (await window.PMW.readLocal('pmwstockmedia/getPMWStockMedia', params)) as string;
};

export const getStatusForPmwStockGetRequest = async (hashId: string): Promise<PMWStockBackendResult> => {
  return (await window.PMW.readLocal('pmwstockmedia/getPMWStockMediaStatus', {
    hashId,
  })) as Promise<PMWStockBackendResult>;
};

export const fetchStockForCarousel = async (params: Record<string, string>): Promise<{hashId: string; pollingKey: string}> => {
  return (await window.PMW.readLocal('stockmedia/getStockForCarouselV2', params)) as {hashId: string; pollingKey: string};
};

export const getStatusForStockCarouselGetRequest = async (initialData: unknown, immediate = false): Promise<StockResponseCarousel> => {
  const record = initialData as Record<string, unknown>;
  const hashId = record.hashId as string;
  return (await window.PMW.readLocal('stockmedia/getStockForCarouselV2Status', {
    hashId,
    ...(immediate ? {streamImmediate: 1} : {}),
  })) as Promise<StockResponseCarousel>;
};
